import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { DayBarChart } from "~/components/day-bar-chart";
import {
  type DayCount,
  fillDateGaps,
  chartDateRange,
} from "~/lib/shared/day-chart";
import { violet } from "~/lib/ui";

export const getCoachesByDay = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<DayCount>> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { coaches } = await import("~/../drizzle/schema");
    const { sql, and, gte, lt, isNotNull } = await import("drizzle-orm");

    const { rangeStart, rangeEnd } = chartDateRange(60, 0);
    const db = await getDBWithInit();

    const rows = await db
      .select({
        date: sql<string>`DATE(${coaches.createdAt})`.as("date"),
        count: sql<number>`COUNT(*)::int`.as("count"),
      })
      .from(coaches)
      .where(
        and(
          gte(coaches.createdAt, rangeStart),
          lt(coaches.createdAt, rangeEnd),
          isNotNull(coaches.approvedAt),
        ),
      )
      .groupBy(sql`DATE(${coaches.createdAt})`)
      .orderBy(sql`DATE(${coaches.createdAt})`);

    return fillDateGaps(rows, {
      daysBefore: 60,
      daysAfter: 0,
      pastColor: violet[500],
      futureColor: violet[300],
    });
  },
);

function CoachesChartPage() {
  const { data } = Route.useLoaderData();
  return (
    <DayBarChart
      data={data}
      noun="Coaches"
      rangeLabel="last 60 days"
      tickInterval={10}
    />
  );
}

export const Route = createFileRoute("/embed/coaches-chart/")({
  head: () => ({
    title: "Coaches Chart - Dream Coach Match",
    meta: [
      {
        name: "description",
        content: "Embedded chart showing new coach registrations over time",
      },
    ],
  }),
  loader: async () => {
    const data = await getCoachesByDay();
    return { data };
  },
  component: CoachesChartPage,
});
